import { message as toaster } from 'antd';
import { API } from 'api';

/**
 * Bulk Upload Slice
 * Handles all bulk upload operations across different entities (vehicles, vendors, etc.)
 */

// Constants
const BULK_UPLOAD_ENDPOINT = '/bulk-upload'; // ✅ Changed to match EXCEPTION_URLS in api.request.js
const API_VERSION = 'v2';
const ERROR_STATUS = 'error';
const DEFAULT_ERROR_MESSAGE = 'Failed to process bulk upload';
const LOG_ERROR_MESSAGE = '❌ [API] Bulk upload API error:';

/**
 * Bulk Upload Vehicles
 * @param {Object} params - Upload parameters
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUploadVehicles =
	({ s3_key, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'vehicle',
			data: {
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: BULK_UPLOAD_ENDPOINT,
			data,
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					const { success_count, error_count, total_count } = res.data || {};

					// Show appropriate message based on results
					if (error_count > 0) {
						toaster.warning(
							`Bulk upload completed: ${success_count} succeeded, ${error_count} failed out of ${total_count} total`
						);
					} else {
						toaster.success(
							res.message || `Successfully uploaded ${success_count} vehicles`
						);
					}
				}

				return res;
			})
			.catch((error) => {
				console.error(LOG_ERROR_MESSAGE, error);

				// Return error in consistent format
				return {
					status: ERROR_STATUS,
					error: {
						code:
							error.response?.data?.error?.code ||
							error.response?.status ||
							'API_ERROR',
						message:
							error.response?.data?.error?.message ||
							error.response?.data?.message ||
							error.message ||
							DEFAULT_ERROR_MESSAGE,
					},
				};
			});
	};

/**
 * Bulk Upload Vendors
 * @param {Object} params - Upload parameters
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUploadVendors =
	({ s3_key, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'vendor',
			data: {
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: BULK_UPLOAD_ENDPOINT,
			data,
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					toaster.success(res.message || 'Vendors uploaded successfully');
				}

				return res;
			})
			.catch((error) => {
				console.error(LOG_ERROR_MESSAGE, error);

				return {
					status: ERROR_STATUS,
					error: {
						code: error.response?.status || 'API_ERROR',
						message:
							error.response?.data?.message || error.message || DEFAULT_ERROR_MESSAGE,
					},
				};
			});
	};

/**
 * Bulk Upload Drivers
 * @param {Object} params - Upload parameters
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {string} params.entity - Entity type from signed URL (e.g., 'driver_bulk_upload_v2')
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUploadDrivers =
	({ s3_key, entity, setErrors }) =>
	async () => {
		// Ensure entity is always set
		const entityToUse = entity || 'driver_bulk_upload_v2';

		const data = {
			upload_type: 'driver',
			data: {
				entity: entityToUse,
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: BULK_UPLOAD_ENDPOINT,
			data,
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					toaster.success(res.message || 'Drivers uploaded successfully');
				}

				return res;
			})
			.catch((error) => {
				console.error(LOG_ERROR_MESSAGE, error);

				return {
					status: ERROR_STATUS,
					error: {
						code: error.response?.status || 'API_ERROR',
						message:
							error.response?.data?.message || error.message || DEFAULT_ERROR_MESSAGE,
					},
				};
			});
	};

/**
 * Bulk Upload Vehicle Types
 * @param {Object} params - Upload parameters
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUploadVehicleTypes =
	({ s3_key, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'vehicle_type',
			data: {
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: BULK_UPLOAD_ENDPOINT,
			data,
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					toaster.success(res.message || 'Vehicle types uploaded successfully');
				}

				return res;
			})
			.catch((error) => {
				console.error(LOG_ERROR_MESSAGE, error);

				return {
					status: ERROR_STATUS,
					error: {
						code: error.response?.status || 'API_ERROR',
						message:
							error.response?.data?.message || error.message || DEFAULT_ERROR_MESSAGE,
					},
				};
			});
	};

/**
 * Generic Bulk Upload
 * Use this for custom upload types
 * @param {Object} params - Upload parameters
 * @param {string} params.upload_type - Type of entity to upload
 * @param {string} params.s3_key - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response
 */
export const bulkUpload =
	({ upload_type, s3_key, entity, setErrors }) =>
	async () => {
		const data = {
			upload_type,
			data: {
				...(entity && { entity }),
				key: s3_key,
			},
		};

		const CREDENTIALS = {
			url: BULK_UPLOAD_ENDPOINT,
			data,
			setErrors,
			version: API_VERSION,
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res && res.status === 'success') {
					toaster.success(res.message || 'Bulk upload completed successfully');
				}

				return res;
			})
			.catch((error) => {
				console.error(LOG_ERROR_MESSAGE, error);

				return {
					status: ERROR_STATUS,
					error: {
						code: error.response?.status || 'API_ERROR',
						message:
							error.response?.data?.message || error.message || DEFAULT_ERROR_MESSAGE,
					},
				};
			});
	};
